import { useState } from "react";
import { useForm } from "react-hook-form";
import { tokens } from "@core/theme/tokens";
import { ApiError } from "@core/http";
import {
  AlertText,
  Checkbox,
  DialogActions,
  DialogHeader,
  EditorModal,
  Field,
  inputStyle,
  Modal,
  PortalButton,
  useToast,
} from "@portal/common";
import { PROGRESS_ERROR_MESSAGES, type ProgressDetail } from "./progressApi";
import { reportError } from "@portal/common/errorMessage";
import { useProgressMutations } from "./progressQueries";
import {
  makeDeliveryFormSchema,
  type DeliveryFormValues,
} from "./progressSchemas";

// SUB-001 paso 2 + PG-002 (resolución humana 2026-09-07): mismo modal
// canónico, acción primaria «Marcar ganador entregado» con el checkbox de
// confirmación del prototipo y el bloque de excedente (avance inicial +
// motivo obligatorio solo si es mayor que cero). Solo se ofrece con la meta
// ya validada; ante `GOAL_UNVALIDATED` se muestra el mensaje de §8 y no se
// reintenta.
export function PrizeDeliveryModal({
  progress,
  surplus,
  onClose,
}: {
  progress: ProgressDetail;
  surplus: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const mutations = useProgressMutations();
  const [blocked, setBlocked] = useState(false);
  const [delivered, setDelivered] = useState<{
    goal: number;
    newProgress: number;
  } | null>(null);
  const schema = makeDeliveryFormSchema(surplus);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<DeliveryFormValues>({
    defaultValues: { confirmed: false, initialProgress: 0, reason: "" },
  });
  const initialProgress = watch("initialProgress") ?? 0;
  const confirmed = watch("confirmed");

  const submit = (value: DeliveryFormValues) => {
    const parsed = schema.safeParse(value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof DeliveryFormValues, {
          message: issue.message,
        });
      }
      return;
    }
    mutations.deliverPrize.mutate(
      {
        id: progress.id,
        input: {
          initialProgress: parsed.data.initialProgress ?? 0,
          ...(parsed.data.reason?.trim()
            ? { initialProgressReason: parsed.data.reason.trim() }
            : {}),
        },
      },
      {
        onSuccess: (result) => {
          toast.show("Premio entregado.");
          if (result.newProgress) {
            setDelivered({
              goal: result.newProgress.goal,
              newProgress: result.newProgress.progress,
            });
          } else {
            onClose();
          }
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === "GOAL_UNVALIDATED") {
            setBlocked(true);
          }
          reportError(toast, error, PROGRESS_ERROR_MESSAGES);
        },
      },
    );
  };

  if (delivered) {
    return (
      // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: el acuse de entrega
      // adopta las tres regiones aunque hoy no llegue a scrollear — el pedido
      // es unicidad de componente, no "solo donde haga falta". No lleva
      // `DialogActions`: su única acción es "Cerrar", no hay un "Cancelar"
      // que oponerle.
      <Modal
        label="Ganador entregado"
        width={520}
        onClose={onClose}
        header={
          // Punto #21 (revisión en vivo 2026-09-22): encabezado común; la
          // bajada sigue debajo, con el aire que antes daba el margen.
          <>
            <DialogHeader
              eyebrow="Gran premio · Avances"
              title="Ganador entregado"
            />
            <div
              style={{
                font: `400 13px/1.6 ${tokens.font.ui}`,
                color: tokens.text.secondary,
                marginTop: 5,
              }}
            >
              El conteo se cerró y se abrió uno nuevo desde hoy. Es el
              comportamiento normal del modelo — sin alarma.
            </div>
          </>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <PortalButton
              type="button"
              tone="primary"
              size="small"
              onClick={onClose}
            >
              Cerrar
            </PortalButton>
          </div>
        }
      >
        <div
          style={{
            padding: 16,
            borderRadius: tokens.radius.sm,
            border: `1px solid ${tokens.gold.line}`,
            background: tokens.gold.dim,
            font: `400 13px ${tokens.font.ui}`,
          }}
        >
          Nuevo conteo: {delivered.newProgress} de {delivered.goal}
        </div>
      </Modal>
    );
  }

  return (
    // Mismas tres regiones, acá con `EditorModal`: es el que aporta el
    // `<form display:contents>` para que "Marcar ganador entregado"
    // (`type="submit"`) siga siendo descendiente del formulario desde el pie
    // fijo.
    <EditorModal
      modal={{ label: "Registrar la entrega", width: 620, onClose }}
      onSubmit={handleSubmit(submit)}
      header={
        // Punto #21 (revisión en vivo 2026-09-22): encabezado común; la
        // línea del miembro sigue debajo, con el aire que antes daba el
        // margen del título.
        <>
          <DialogHeader
            eyebrow="Gran premio · Avances"
            title="Registrar la entrega"
          />
          <div
            style={{
              font: `400 12px/1.55 ${tokens.font.ui}`,
              color: tokens.text.tertiary,
              marginTop: 5,
            }}
          >
            {progress.member.legalName} · meta validada.
          </div>
        </>
      }
      footer={
        <DialogActions onCancel={onClose}>
          <PortalButton
            type="submit"
            tone="primary"
            size="small"
            disabled={blocked || mutations.deliverPrize.isPending}
          >
            {mutations.deliverPrize.isPending
              ? "Entregando…"
              : "Marcar ganador entregado"}
          </PortalButton>
        </DialogActions>
      }
    >
      {surplus > 0 ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "12px 16px",
              borderRadius: tokens.radius.sm,
              border: `1px solid ${tokens.surface.line2}`,
              marginBottom: 16,
              font: `400 13px ${tokens.font.ui}`,
            }}
          >
            <span>Excedente sobre la meta</span>
            <b>{surplus}</b>
          </div>
          <Field
            label="Avance inicial reconocido"
            error={errors.initialProgress?.message}
          >
            <input
              type="number"
              min={0}
              max={surplus}
              style={inputStyle}
              {...register("initialProgress")}
            />
          </Field>
          <Field
            label={`Motivo${initialProgress > 0 ? " *" : ""}`}
            error={errors.reason?.message}
          >
            <textarea
              style={{
                ...inputStyle,
                minHeight: 66,
                resize: "vertical" as const,
              }}
              {...register("reason")}
            />
          </Field>
        </>
      ) : null}

      <div style={{ margin: "12px 0 0" }}>
        <Checkbox
          name="confirmed"
          checked={confirmed}
          onChange={(next) =>
            setValue("confirmed", next, { shouldDirty: true })
          }
          label="Confirmo la entrega del gran premio"
        />
      </div>
      {errors.confirmed ? (
        // Punto #4C: el aviso de error del checkbox pasa a `AlertText` — el
        // `marginTop: -8` que compensaba el margen del bloque anterior ya no
        // hace falta, ese margen se fue con la migración a las tres regiones.
        <AlertText style={{ marginTop: 8 }}>
          {errors.confirmed.message}
        </AlertText>
      ) : null}
    </EditorModal>
  );
}
